import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { MinMaxSelect } from '../../inputs/MinMaxSelect'
import styles from '../../search.module.scss'
import { FilterDropdown } from '@/components/search/FilterDropdown'
import { FILTER_PERFORMANCE } from '@/lib/constants'
import { filterLabelPrettyName } from '@/lib/utilities/search'
import {
  selectFullActivePerformance,
  selectPerformanceSearchFilter,
} from '@/store/selectors/search'
import { PerformanceFilterEngineItemProps } from '@/types/search'

type EngineSizeProps = {
  isFilterOpen: boolean
  toggleFilter: () => void
  onChange: (params: {
    type: 'multi' | 'single' | 'vehiclePriceTag'
    filter: string
    value: string | number
  }) => void
}

export const EngineSize = ({ isFilterOpen, toggleFilter, onChange }: EngineSizeProps) => {
  const availableEngine = useSelector(selectPerformanceSearchFilter)
  const activePerformance = useSelector(selectFullActivePerformance)

  const [activeEngineMin, setActiveEngineMin] = useState<
    PerformanceFilterEngineItemProps | undefined
  >(undefined)
  const [activeEngineMax, setActiveEngineMax] = useState<
    PerformanceFilterEngineItemProps | undefined
  >(undefined)
  const [availableEngineMin, setAvailableEngineMin] = useState<PerformanceFilterEngineItemProps[]>(
    [],
  )
  const [availableEngineMax, setAvailableEngineMax] = useState<PerformanceFilterEngineItemProps[]>(
    [],
  )
  const [minVal, setMinVal] = useState<number | undefined>(undefined)
  const [maxVal, setMaxVal] = useState<number | undefined>(undefined)

  useEffect(() => {
    if (activePerformance) {
      const activeEngineMin = activePerformance.filter(
        (eng: PerformanceFilterEngineItemProps) => eng.type === 20,
      )[0]
      const activeEngineMax = activePerformance.filter(
        (eng: PerformanceFilterEngineItemProps) => eng.type === 21,
      )[0]

      setActiveEngineMin(activeEngineMin)
      setMinVal(activeEngineMin?.id)

      setActiveEngineMax(activeEngineMax)
      setMaxVal(activeEngineMax?.id)
    }
  }, [activePerformance])

  useEffect(() => {
    if (availableEngine) {
      setAvailableEngineMin(
        availableEngine.filter((eng: PerformanceFilterEngineItemProps) => eng.type === 20),
      )
      setAvailableEngineMax(
        availableEngine.filter((eng: PerformanceFilterEngineItemProps) => eng.type === 21),
      )
    }
  }, [availableEngine])

  const handleMinInput = (value: number) => {
    const newValue = value ? value : undefined

    setMinVal(newValue)
    if (activeEngineMin) {
      onChangeInputValue(activeEngineMin.id)
    }
    onChangeInputValue(newValue)
  }

  const handleMaxInput = (value: number) => {
    const newValue = value ? value : undefined

    setMaxVal(newValue)
    if (activeEngineMax) {
      onChangeInputValue(activeEngineMax.id)
    }
    onChangeInputValue(newValue)
  }

  const onChangeInputValue = (newValue: number) => {
    onChange({
      type: 'multi',
      filter: FILTER_PERFORMANCE,
      value: newValue ? newValue : 0,
    })
  }

  return (
    <FilterDropdown
      title="Engine size"
      toggleFilter={toggleFilter}
      isFilterOpen={isFilterOpen}
      hasSelectedOptions={!!minVal || !!maxVal}
      selectedOptions={
        minVal || maxVal
          ? [
              `${filterLabelPrettyName(
                FILTER_PERFORMANCE,
                minVal ? availableEngineMin.filter(eng => eng.id === minVal)?.[0]?.name : 0,
                'MIN_ENGINE',
              )}-${filterLabelPrettyName(
                FILTER_PERFORMANCE,
                maxVal
                  ? availableEngineMax.filter(eng => eng.id === maxVal)?.[0]?.name
                  : availableEngineMax.at(-1)?.name,
                'MAX_ENGINE',
              )}`,
            ]
          : ['All']
      }
      maximumNumberOfVisibleSelectedOptions={1}>
      {availableEngineMax.length > 0 || availableEngineMin.length > 0 ? (
        <div className={styles.Budget}>
          <MinMaxSelect
            activeMin={minVal}
            activeMax={maxVal}
            handleMinInput={handleMinInput}
            handleMaxInput={handleMaxInput}
            availableMaximum={availableEngineMax}
            availableMinimum={availableEngineMin}
          />
        </div>
      ) : (
        <p className={styles['error-text']}>
          {'There are no "Engine Size" options available based on your current filter selections'}
        </p>
      )}
    </FilterDropdown>
  )
}
